/* AppraisalComponents/SectionAccordion.jsx
 * Phase C · C4f (2026-02c) — WORK_TYPES.map inner accordion body
 * extracted verbatim from AppraisalComponentsTab.js:3693-4083.
 *
 * Owns the section shell for every work-type: header, expand toggle,
 * badges, section-body adornments, ComponentLineRow render loop, and
 * add/duplicate buttons. Kept as ONE generic component (rather than
 * per-section files) because ~80% of the JSX is shared across every
 * section — the specialisation is a handful of `wt.key === 'X'` guards
 * for the extracted adornments (SheetSectionProfilePicker,
 * FasteningCascadeBar, AquasealExtraClipsBar, SpoutingBracketSummary,
 * etc.). Splitting further would duplicate the shared JSX 17 times.
 *
 * State stays in the parent. A single `ctx` bag prop carries everything
 * (avoids drilling 60+ individual props). ctx is memoised in the
 * parent so the accordion only re-renders when a section-relevant
 * slice changes.
 */
import React from 'react';
import { ChevronDown, ChevronRight, Plus, Copy } from 'lucide-react';
import { toast } from 'sonner';
import ComponentLineRow from '@/components/shared/ComponentRow/ComponentLineRow';
import SpoutingGutterDetailsBar from '@/components/SpoutingGutterDetailsBar';
import { MATERIAL_SECTIONS } from '@/components/shared/ComponentRow/helpers';
import { fmt, isAutoBracketLine, withMitreTag, lineTotals, isGutterLengthLine, spoutingRowsFor } from './helpers';
import { FasteningTotalScrewsBadge, FasteningCascadeBar } from './FasteningSectionAdornments';
import { SheetSectionProfilePicker, SheetSectionTotals } from './SheetSectionAdornments';
import { AquasealExtraClipsBar } from './AquasealExtraClipsBar';
import { FlashingEdgingBar } from './FlashingEdgingBar';
import { SpoutingBracketSummary } from './SpoutingBracketSummary';
import { MultidekClipsBar } from './MultidekClipsBar';
import { BedPointMaterialsBar } from './BedPointMaterialsBar';
import { FoamFillerBar } from './FoamFillerBar';


export function SectionAccordion({ wt, ctx }) {
  const {
    // state slices
    groups, rates, openTypes,
    sheetSectionProfile, setSheetSectionProfile, sheetProfileNames, sheetProfileMap,
    roofProfile, roofType, roofBrand, roofSubtype, roofM2, primaryMaterial, tileProfileOverride,
    materialListBySection, materialBySection, setMaterialBySection,
    sectionShowAll, setSectionShowAll,
    gutterCtx, setGutterCtx,
    flashingTemplates, valleyProfileOptions, bargeProfileOptions,
    quoteType, appraisalId, facePinLengths, profileLists,
    selectedKeys,
    // setters
    setGroups, setDirty, setRates,
    // callbacks
    toggleOpen, addLine, removeLine, duplicateLastLine, updateLine, onPickType,
    cascadeFieldToSiblings, toggleSelected, selectAllInSection,
    onLinkDektite, onUnlinkDektite, onChangeDektite,
    onLinkBacktray, onUnlinkBacktray, onChangeBacktray,
    pushClearSiliconeToBom, upsertExtraClipsLine, upsertMultidekAuxLines,
    upsertFoamFillerLines, upsertEdgingLines, upsertBedPointAuxLines,
    pushBracketsToBom,
    scrollToFastMenu, jumpToDiagram,
    api, isAdmin,
  } = ctx;

  const lines = groups[wt.key] || [];
  const isOpen = openTypes.has(wt.key);
  const wtRates = rates[wt.key] || [];
  const sectionTotals = lines.reduce((acc, l) => {
    const sectionMitreExtraM = wt.key === 'Spouting' ? (Number(gutterCtx?.mitreExtraMm) || 0) / 1000 : 0;
    const t = lineTotals(l, sectionMitreExtraM);
    return { ...acc, customer: acc.customer + t.customer_charge, cost: acc.cost + t.cost_to_us };
  }, { customer: 0, cost: 0 });

  return (
    <div className={`bg-white border rounded-md overflow-hidden card-lift ${lines.length > 0 ? 'border-[#E2E2DF]' : 'border-dashed border-slate-200'}`} data-testid={`comp-section-${wt.key}`}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => toggleOpen(wt.key)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleOpen(wt.key); } }}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <span className="w-1 h-6 rounded-full" style={{ background: wt.colour }} />
          {isOpen ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
          <h3 className="text-sm font-bold text-slate-900 font-[Manrope]">{wt.label || wt.key}</h3>
          {lines.length > 0 && <span className="text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-orange-50 text-[#D35400] ring-1 ring-orange-100 font-mono-num">{lines.length}</span>}
        </div>
        <div className="flex items-center gap-3 text-xs">
          {wt.key === 'Fastening' && <FasteningTotalScrewsBadge lines={lines} />}
          {sectionTotals.customer > 0 && <><span className="text-slate-500">Charge</span><span className="font-bold text-slate-800 font-mono-num">{fmt(sectionTotals.customer)}</span></>}
          {false && wt.key === 'Aquaseal' && lines.length > 0 && (
            <button
              type="button"
              data-testid="aquaseal-push-clear-silicone"
              onClick={(e) => { e.stopPropagation(); pushClearSiliconeToBom(); }}
              title="Push Clear Silicone tube count to BOM (Misc section) based on the total dektite qty here. Uses the appraisal's silicone_buffer_pct for the next-tube nudge."
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal-600 text-white hover:bg-teal-700 transition-colors"
            >
              Push Clear Silicone → Misc
            </button>
          )}
          <button
            type="button"
            data-testid={`section-jump-menu-${wt.key}`}
            onClick={(e) => { e.stopPropagation(); scrollToFastMenu(); }}
            title="Back to the fast-menu chip bar at the top of Components."
            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
          >↑</button>
          <button
            type="button"
            data-testid={`section-jump-diagram-${wt.key}`}
            onClick={(e) => { e.stopPropagation(); jumpToDiagram(); }}
            title="Jump to the annotated roof diagram at the top of the page."
            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors"
          >📐</button>
        </div>
      </div>

      {isOpen && (
        <div className="border-t border-[#E2E2DF] bg-slate-50/30 p-3 space-y-2">
          {wt.key === 'Aquaseal' && (
            <AquasealExtraClipsBar lines={groups.Aquaseal || []} rates={rates.Aquaseal || []} onCommit={upsertExtraClipsLine} />
          )}
          {wt.key === 'Sheet' && (
            <MultidekClipsBar lines={groups.Sheet || []} rates={rates.Sheet || []} onCommit={upsertMultidekAuxLines} />
          )}
          {wt.key === 'Sheet' && (
            <FoamFillerBar lines={groups.Sheet || []} rates={rates.Sheet || []} onCommit={upsertFoamFillerLines} />
          )}
          {wt.key === 'Flashing' && (
            <FlashingEdgingBar lines={groups.Flashing || []} rates={rates.Flashing || []} templates={flashingTemplates} onCommit={upsertEdgingLines} />
          )}
          {(wt.key === 'Ridge' || wt.key === 'Barge') && (
            <BedPointMaterialsBar
              section={wt.key}
              lines={groups[wt.key] || []}
              miscRates={rates.Misc || []}
              defaults={wt.key === 'Ridge'
                ? { mortar_cov: 3.5, pointing_cov: 18 }
                : { mortar_cov: 10,  pointing_cov: 35 }}
              onCommit={(payload) => upsertBedPointAuxLines(wt.key, payload)}
            />
          )}
          {wt.key === 'Spouting' && (
            <SpoutingGutterDetailsBar api={api} appraisalId={appraisalId} onContextChange={setGutterCtx} />
          )}
          {wt.key === 'Sheet' && (
            <SheetSectionProfilePicker
              sheetSectionProfile={sheetSectionProfile}
              setSheetSectionProfile={setSheetSectionProfile}
              sheetProfileNames={sheetProfileNames}
              roofProfile={roofProfile}
              setGroups={setGroups}
              setDirty={setDirty}
            />
          )}
          {wt.key === 'Sheet' && (
            <SheetSectionTotals sheetLines={groups.Sheet || []} roofM2={roofM2} />
          )}
          {/* Per-section "default material for new lines" picker. */}
          {MATERIAL_SECTIONS.includes(wt.key) && (materialListBySection[wt.key] || []).length > 1 && (
            <div data-testid={`material-picker-${wt.key.toLowerCase()}`} className="rounded-md border border-slate-200 bg-white px-3 py-2 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Material</span>
              <button
                type="button"
                data-testid={`material-${wt.key.toLowerCase()}-all`}
                onClick={() => setMaterialBySection(s => ({ ...s, [wt.key]: 'all' }))}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${(materialBySection[wt.key] || 'all') === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
              >
                All <span className="opacity-70 ml-0.5">({wtRates.length})</span>
              </button>
              {(materialListBySection[wt.key] || []).map(({ key, count }) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`material-${wt.key.toLowerCase()}-${key.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setMaterialBySection(s => ({ ...s, [wt.key]: key }))}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${materialBySection[wt.key] === key ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
                >
                  {key} <span className="opacity-70 ml-0.5">({count})</span>
                </button>
              ))}
              {wt.key === 'Flashing' && roofType && (
                <span className="ml-auto text-[10px] text-slate-400 italic">From roof: <span className="font-semibold text-slate-600 not-italic">{roofType}</span> · each line can override below</span>
              )}
              <button
                type="button"
                data-testid={`section-show-all-${wt.key.toLowerCase()}`}
                onClick={() => setSectionShowAll(s => ({ ...s, [wt.key]: !s[wt.key] }))}
                title={sectionShowAll[wt.key]
                  ? `Cascade bypassed for every ${wt.key} row — showing all ${wtRates.length} rates. Click to re-engage cascade.`
                  : `Bypass cascade for every ${wt.key} row — show all ${wtRates.length} rate rows in the catalogue.`}
                className={`ml-auto inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border transition-all ${
                  sectionShowAll[wt.key]
                    ? 'bg-amber-100 text-amber-800 border-amber-300 ring-1 ring-amber-200'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-amber-300 hover:text-amber-700'
                }`}
              >
                {sectionShowAll[wt.key] ? `▴ Showing all ${wtRates.length}` : `▾ See all ${wtRates.length}`}
              </button>
            </div>
          )}
          {wt.key === 'Fastening' && (
            <FasteningCascadeBar
              wtRates={wtRates}
              quoteType={quoteType}
              roofProfile={roofProfile}
              sectionShowAllFastening={!!sectionShowAll.Fastening}
              onToggleSectionShowAll={() => setSectionShowAll(s => ({ ...s, Fastening: !s.Fastening }))}
            />
          )}
          {lines.length === 0 && <p className="text-xs text-slate-400 px-2 py-3">No {wt.key.toLowerCase()} lines yet.</p>}
          {lines.length > 0 && (
            <div className="flex items-center gap-2 px-2 pb-1 text-[10px] uppercase tracking-wide text-slate-400">
              <input
                type="checkbox"
                data-testid={`components-select-all-${wt.key.toLowerCase()}`}
                aria-label={`Select all ${wt.key} lines`}
                checked={lines.every((l) => selectedKeys.has(l._key))}
                onChange={(e) => selectAllInSection(wt.key, e.target.checked)}
                className="w-3.5 h-3.5 accent-rose-600"
              />
              <span>Select all in section</span>
              {wt.key === 'Spouting' && (() => {
                const mitreCandidates = lines.filter(isGutterLengthLine);
                if (mitreCandidates.length === 0) return null;
                const allTwoMitre = mitreCandidates.every(l => !l.stop_end_left && !l.stop_end_right);
                return (
                  <label
                    data-testid="components-select-all-spouting-mitres"
                    className={`ml-3 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border cursor-pointer transition-colors ${
                      allTwoMitre
                        ? 'bg-cyan-100 text-cyan-900 border-cyan-300'
                        : 'bg-white text-slate-500 border-slate-200 hover:border-cyan-300 hover:text-cyan-700'
                    }`}
                    title={allTwoMitre
                      ? `Set both ends of all ${mitreCandidates.length} gutter runs to Stop End.`
                      : `Set both ends of every gutter run to Mitre. Each run gets +${((gutterCtx?.mitreExtraMm ?? 300) / 500).toFixed(2)}m extra length.`}
                  >
                    <input
                      type="checkbox"
                      checked={allTwoMitre}
                      onChange={(e) => {
                        const target = e.target.checked ? 2 : 0;
                        const stop = target === 0;
                        setGroups(prev => {
                          const next = { ...prev };
                          next.Spouting = (prev.Spouting || []).map(l =>
                            isGutterLengthLine(l) ? { ...l, mitre_count: target, stop_end_left: stop, stop_end_right: stop, notes: withMitreTag(l.notes, false) } : l
                          );
                          return next;
                        });
                        setDirty(true);
                        toast.success(`Set ${mitreCandidates.length} gutter run${mitreCandidates.length === 1 ? '' : 's'} to ${stop ? 'Stop End' : 'Mitre'} both ends.`);
                      }}
                      className="w-3 h-3 accent-cyan-600"
                    />
                    <span>◢ All mitres</span>
                  </label>
                );
              })()}
            </div>
          )}
          {lines.map((line, idx) => {
            // Iter142 — Spouting cascade: narrow the wtRates list
            // passed down to each line so its Type dropdown only
            // shows rate rows matching the Gutter Details bar.
            let ratesForRow = wtRates;
            if (wt.key === 'Spouting' && gutterCtx && line.pin_id) {
              // Diagram gutter runs list gutter lengths only, for the row's own profile (or the banner's).
              // Manually added rows keep the full Spouting rate table.
              const gl = isGutterLengthLine(line);
              const rowProfile = gl && (gutterCtx.profileLabels || []).includes(line.profile) ? line.profile : gutterCtx.profileLabel;
              const narrowed = spoutingRowsFor(wtRates, { ...gutterCtx, profileLabel: rowProfile }, gl);
              if (narrowed.length > 0) ratesForRow = narrowed;
            }
            return (
              <div key={line._key} className="flex items-start gap-2" data-testid={`components-row-wrapper-${wt.prefix}${idx + 1}`}>
                <input
                  type="checkbox"
                  data-testid={`components-row-checkbox-${wt.prefix}${idx + 1}`}
                  aria-label={`Select ${wt.prefix}${idx + 1}`}
                  checked={selectedKeys.has(line._key)}
                  onChange={() => toggleSelected(line._key)}
                  className="mt-3 w-3.5 h-3.5 accent-rose-600 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  {wt.key === 'Spouting' && <SpoutRateSource line={line} rates={wtRates} />}
                  <ComponentLineRow
                    key={line._key} api={api} line={line} rates={ratesForRow}
                    profiles={wt.key === 'Sheet' ? sheetProfileNames : wt.key === 'Spouting' ? (gutterCtx?.profileLabels || []) : (profileLists[wt.key] || [])}
                    sheetProfileMap={wt.key === 'Sheet' ? sheetProfileMap : null}
                    colour={wt.colour} ref_id={`${wt.prefix}${idx + 1}`} isAdmin={isAdmin}
                    materialList={MATERIAL_SECTIONS.includes(wt.key) ? (materialListBySection[wt.key] || []) : null}
                    sectionKey={wt.key} siblingCount={lines.length}
                    roofTypeCtx={roofType} roofBrandCtx={roofBrand} roofProfileCtx={roofProfile}
                    sheetSectionProfile={wt.key === 'Sheet' ? sheetSectionProfile : ''}
                    sectionShowAll={!!sectionShowAll[wt.key]}
                    mitreExtraM={wt.key === 'Spouting' ? (Number(gutterCtx?.mitreExtraMm) || 0) / 1000 : 0}
                    roofM2={roofM2}
                    facePinLengths={wt.key === 'Fastening' ? facePinLengths : []}
                    flashingTemplates={(wt.key === 'Flashing' || wt.key === 'Aquaseal' || wt.key === 'Valley' || wt.key === 'Barge') ? flashingTemplates : []}
                    valleyProfileOptions={wt.key === 'Valley' ? valleyProfileOptions : []}
                    bargeProfileOptions={wt.key === 'Barge' ? bargeProfileOptions : []}
                    primaryMaterialCtx={primaryMaterial || ''}
                    onRequestRatesRefresh={async (workType) => {
                      try {
                        const { data } = await api.get(`/component-rates?component_type=${encodeURIComponent(workType)}`);
                        setRates(prev => ({ ...prev, [workType]: data }));
                      } catch (e) { /* swallow — UI keeps stale list */ }
                    }}
                    flashingLines={wt.key === 'Aquaseal' ? (groups.Flashing || []) : []}
                    sheetLines={wt.key === 'Underlay' ? (groups.Sheet || []) : []}
                    quoteTypeCtx={quoteType} appraisalIdCtx={appraisalId}
                    roofSubtypeCtx={roofSubtype} tileProfileOverrideCtx={tileProfileOverride}
                    onUpdate={patch => updateLine(wt.key, line._key, patch)}
                    onPickType={tn => onPickType(wt.key, line._key, tn)}
                    onRemove={() => removeLine(wt.key, line._key)}
                    onApplyToAll={(field, value, opts) => cascadeFieldToSiblings(wt.key, line._key, field, value, opts)}
                    onLinkDektite={wt.key === 'Flashing' ? (payload => onLinkDektite(line._key, payload)) : undefined}
                    onUnlinkDektite={wt.key === 'Flashing' ? ((uid, opts) => onUnlinkDektite(line._key, uid, opts)) : undefined}
                    onChangeDektite={wt.key === 'Flashing' ? ((oldUid, payload) => onChangeDektite(line._key, oldUid, payload)) : undefined}
                    onLinkBacktray={wt.key === 'Aquaseal' ? (payload => onLinkBacktray(line._key, payload)) : undefined}
                    onUnlinkBacktray={wt.key === 'Aquaseal' ? (opts => onUnlinkBacktray(line._key, opts)) : undefined}
                    onChangeBacktray={wt.key === 'Aquaseal' ? (payload => onChangeBacktray(line._key, payload)) : undefined}
                  />
                </div>
              </div>
            );
          })}
          {wt.key === 'Spouting' && (
            <SpoutingBracketSummary
              api={api}
              appraisalId={appraisalId}
              lines={lines}
              gutterCtx={gutterCtx}
              rates={rates.Spouting || []}
              onPushToBom={pushBracketsToBom}
              onConfigPatched={(patched) => setGutterCtx(prevCtx => ({
                ...(prevCtx || {}),
                dropperRivets:        patched.dropper_rivets ?? prevCtx?.dropperRivets,
                dropperSiliconePct:   patched.dropper_silicone_pct ?? prevCtx?.dropperSiliconePct,
                mitrePairRivets:      patched.mitre_pair_rivets ?? prevCtx?.mitrePairRivets,
                mitrePairSiliconePct: patched.mitre_pair_silicone_pct ?? prevCtx?.mitrePairSiliconePct,
                stopEndRivets:        patched.stop_end_rivets ?? prevCtx?.stopEndRivets,
                stopEndSiliconePct:   patched.stop_end_silicone_pct ?? prevCtx?.stopEndSiliconePct,
              }))}
            />
          )}
          <div className="flex items-center gap-3">
            <button type="button" data-testid={`add-${wt.key}-line`} onClick={() => addLine(wt.key)} className="flex items-center gap-1.5 text-xs font-semibold text-[#D35400] hover:text-[#A04000] px-2 py-1.5 transition-colors">
              <Plus size={13} strokeWidth={2.5} /> Add {wt.key.toLowerCase()} line
            </button>
            {lines.length > 0 && (
              <button type="button" data-testid={`duplicate-${wt.key}-line`} onClick={() => duplicateLastLine(wt.key)} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-white px-2 py-1.5 rounded-md ring-1 ring-[#E2E2DF] transition-colors" title="Add a new line that copies the last entry">
                <Copy size={12} strokeWidth={2.5} /> Duplicate last
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Shows where a Spouting row's price comes from (catalogue row + its cost / sell / labour).
function SpoutRateSource({ line, rates }) {
  if (!line.type_name) return null;
  const r = (rates || []).find(x => x.type_name === line.type_name);
  const m = (v) => `$${(Number(v) || 0).toFixed(2)}`;
  if (!r) {
    return (
      <div data-testid={`spout-rate-source-missing-${line.ref_id || line._key}`}
           className="mb-1 text-[10px] px-2 py-1 rounded border border-amber-300 bg-amber-50 text-amber-800">
        Price {m(line.materials_rate)} sell · {m(line.buy_cost)} cost · {m(line.labour_rate)} labour is NOT from a catalogue row
        ("{line.type_name}" isn't in the Spouting rate table) — pick a Type to re-price.
      </div>
    );
  }
  return (
    <div data-testid={`spout-rate-source-${line.ref_id || line._key}`} className="mb-1 text-[10px] text-slate-500">
      Rate from catalogue: <strong className="text-slate-700">{r.type_name}</strong> · cost {m(r.buy_cost)} · sell {m(r.materials_rate)} · labour {m(r.labour_rate)}
    </div>
  );
}
